import { ListItemIcon, ListItemText, Menu, MenuItem, SxProps, Theme } from '@mui/material';
import { v2MenuItemDangerClass, V2ThemeProvider } from '@onevision-resources/design-theme';
import { Bookmark, EllipsisVertical, Trash2 } from 'lucide-react';
import { DragEvent, useRef, useState } from 'react';
import { Pill } from 'src/components/Pill';
import { AttachmentHoverCard } from 'src/pages/Conversations/SidePanel/ConversationDetails/Attachments/attachment-hover-card';
import { AttachmentKindIcon } from 'src/pages/Conversations/SidePanel/ConversationDetails/Attachments/attachment-kind-icon';
import type { AttachmentPillModel } from 'src/pages/Conversations/SidePanel/ConversationDetails/Attachments/attachment-utils';
import { PillHoverCard } from 'src/pages/Conversations/SidePanel/ConversationDetails/properties/pill-hover-card';
import { setConversationAttachmentDragData } from 'src/utils/conversation-attachment-drag';
import { useHoverCardDelay } from 'src/utils/hooks/use-hover-card-delay';

const ATTACHMENT_HOVER_CARD_OPEN_DELAY_MS = 1000;
const HOVER_ACTION_ICON_SIZE = 11;

const attachmentPillSx: SxProps<Theme> = {
  '& .pill-label': { maxWidth: '7.375rem' },
};

interface AttachmentPillProps {
  attachment: AttachmentPillModel;
  /** Also enables dragging the pill onto Site Files. */
  onAddToSiteFiles?: () => void;
  /** When provided, the menu offers a permanent delete (row + S3 file). */
  onDelete?: () => void;
}

export const AttachmentPill = ({
  attachment,
  onAddToSiteFiles,
  onDelete,
}: AttachmentPillProps) => {
  const pillRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const hoverCard = useHoverCardDelay({ openDelayMs: ATTACHMENT_HOVER_CARD_OPEN_DELAY_MS });

  const canMoveToSiteFiles = Boolean(onAddToSiteFiles);
  const hasMenu = canMoveToSiteFiles || Boolean(onDelete);

  const openMenu = () => {
    hoverCard.closeNow();
    setMenuOpen(true);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const handleAddToSiteFiles = () => {
    onAddToSiteFiles?.();
    closeMenu();
  };

  const handleDelete = () => {
    // Close first: the confirmation dialog owns the interaction from here.
    closeMenu();
    onDelete?.();
  };

  const handleDragStart = (event: DragEvent<HTMLButtonElement>) => {
    hoverCard.closeNow();

    if (canMoveToSiteFiles) {
      setConversationAttachmentDragData(event.dataTransfer, attachment.siteFilePayload);
    }
  };

  return (
    <>
      <PillHoverCard
        hoverCard={hoverCard}
        card={
          <AttachmentHoverCard
            fileName={attachment.fileName}
            kind={attachment.kind}
            displayDate={attachment.displayDate}
          />
        }
      >
        <Pill
          ref={pillRef}
          data-testid="conversation-attachment-pill"
          label={attachment.fileName}
          icon={<AttachmentKindIcon kind={attachment.kind} size={13} />}
          href={attachment.url}
          target={attachment.url ? '_blank' : undefined}
          rel={attachment.url ? 'noreferrer' : undefined}
          aria-label={attachment.url ? `Open ${attachment.fileName}` : undefined}
          draggable={canMoveToSiteFiles}
          onDragStart={handleDragStart}
          hoverAction={
            hasMenu
              ? {
                  icon: <EllipsisVertical size={HOVER_ACTION_ICON_SIZE} />,
                  label: `More options for ${attachment.fileName}`,
                  onClick: openMenu,
                }
              : undefined
          }
          onMouseEnter={hoverCard.openSoon}
          onMouseLeave={hoverCard.closeSoon}
          onFocus={hoverCard.openSoon}
          onBlur={hoverCard.closeSoon}
          sx={attachmentPillSx}
        />
      </PillHoverCard>

      {hasMenu ? (
        <V2ThemeProvider>
          <Menu
            anchorEl={pillRef.current}
            open={menuOpen}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {onAddToSiteFiles ? (
              <MenuItem onClick={handleAddToSiteFiles}>
                <ListItemIcon>
                  <Bookmark />
                </ListItemIcon>
                <ListItemText>Add to Site Files</ListItemText>
              </MenuItem>
            ) : null}
            {onDelete ? (
              <MenuItem
                className={v2MenuItemDangerClass}
                data-testid="conversation-attachment-delete"
                onClick={handleDelete}
              >
                <ListItemIcon>
                  <Trash2 />
                </ListItemIcon>
                <ListItemText>Delete</ListItemText>
              </MenuItem>
            ) : null}
          </Menu>
        </V2ThemeProvider>
      ) : null}
    </>
  );
};
